import axios from 'axios';
import { GamesResponse, BalanceResponse, LaunchRequest, LaunchResponse, CurrenciesResponse, LoginRequest, LoginResponse, UserResponse, GameStatsResponse, GamePeriodStatsRequest, GamePeriodStatsResponse, GameDetailStatsRequest, GameDetailStatsResponse, GameUserStatsRequest, GameUserStatsResponse, GameUserTopRequest, GameUserTopResponse, IntegratorsResponse, CreateGameRequest, CreateGameResponse, CreateCurrencySetting, GenerateTokenRequest, GenerateTokenResponse, GameStatisticGroupedByTimeRequest, GameStatisticGroupedByTimeResponse, DailyUserAnalyticsRequest, DailyUserAnalyticsResponse, CohortRetentionRequest, CohortRetentionResponse } from './types';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:19934';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Include cookies for session management
});

export const apiService = {
  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post('/api/login', credentials);
    return response.data;
  },

  async logout(): Promise<{ success: boolean }> {
    const response = await api.post('/api/logout');
    return response.data;
  },

  async getUser(): Promise<UserResponse> {
    const response = await api.get('/api/user');
    return response.data;
  },

  async getBalance(): Promise<BalanceResponse> {
    const response = await api.get('/api/balance');
    return response.data;
  },

  // Game endpoints
  async getGames(): Promise<GamesResponse> {
    const response = await api.get('/api/games');
    return response.data;
  },

  async launchGame(request: LaunchRequest): Promise<LaunchResponse> {
    const response = await api.post('/api/games/launch', request);
    return response.data;
  },

  async generateToken(request: GenerateTokenRequest): Promise<GenerateTokenResponse> {
    const response = await api.post('/api/games/token', request);
    return response.data;
  },

  // Currency endpoints
  async getCurrencies(): Promise<CurrenciesResponse> {
    const response = await api.get('/api/currencies');
    return response.data;
  },

  async saveCurrency(currency: string): Promise<{ success: boolean; currency: string }> {
    const response = await api.post('/api/currency', { currency });
    return response.data;
  },

  // Admin endpoints
  async getGameStats(): Promise<GameStatsResponse> {
    const response = await api.get('/api/admin/game-stats');
    return response.data;
  },

  async getGamePeriodStats(request: GamePeriodStatsRequest): Promise<GamePeriodStatsResponse> {
    const response = await api.post('/api/admin/game-stats/period', request);
    return response.data;
  },

  async getGameDetailStats(request: GameDetailStatsRequest): Promise<GameDetailStatsResponse> {
    const response = await api.post('/api/admin/game-stats/detail', request);
    return response.data;
  },

  async getGameUserStats(request: GameUserStatsRequest): Promise<GameUserStatsResponse> {
    const response = await api.post('/api/admin/game-stats/users', request);
    return response.data;
  },

  async getGameUserTop(request: GameUserTopRequest): Promise<GameUserTopResponse> {
    const response = await api.post('/api/admin/game-stats/users/top', request);
    return response.data;
  },

  async getIntegrators(): Promise<IntegratorsResponse> {
    const response = await api.get('/api/admin/integrators');
    return response.data;
  },

  async createGame(request: CreateGameRequest): Promise<CreateGameResponse> {
    const response = await api.post('/api/admin/games/create', request);
    return response.data;
  },

  async getCurrencySettings(gameId: number): Promise<CreateCurrencySetting[]> {
    const response = await api.get(`/api/admin/games/currency-settings?game_id=${encodeURIComponent(gameId)}`);
    return response.data;
  },

  async getGameStatsGrouped(request: GameStatisticGroupedByTimeRequest): Promise<GameStatisticGroupedByTimeResponse> {
    const response = await api.post('/api/admin/game-stats/grouped', request);
    return response.data;
  },

  async getDailyUserAnalytics(request: DailyUserAnalyticsRequest): Promise<DailyUserAnalyticsResponse> {
    const response = await api.post('/api/admin/game-stats/users/daily', request);
    return response.data;
  },

  async getCohortRetention(request: CohortRetentionRequest): Promise<CohortRetentionResponse> {
    const response = await api.post('/api/admin/game-stats/users/retention', request);
    return response.data;
  },
};

export default apiService;
